// Public quote landing page — what the client sees when they click the email
// link. No auth: only the publicToken in the URL grants access.
//
// Marks viewedAt on first GET, acceptedAt + acceptedItemId when the client
// picks an option.

import { useEffect, useState } from 'react';
import { CheckCircle, Shield, Calendar, Mail, FileDown } from 'lucide-react';
import { viewQuotePublic, acceptQuoteOption, declineQuote } from '../api.js';
import { publicCoverageFieldsFor, coverageLabel } from './quoteCoverageFields.js';
import { toast } from './Toast.jsx';
import { leerDetalleDeCotizacion, tituloDelDetalle } from '../cotizadorCierre.js';

function fmtMoney(n) {
  if (n == null || isNaN(Number(n))) return '$0.00';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Los textos de la página, en el idioma de la ficha del cliente (`data.lang`,
// el mismo con el que salió el correo). Paula Angarita, 2026-10-06: con la
// ficha en Español, la cotización llegaba con el encabezado en inglés.
const TEXTOS = {
  en: {
    loading: 'Loading your quote…', notFound: 'Quote not found',
    notFoundBody: 'This link may be invalid or expired. Please contact your agent.',
    hi: 'Hi', yourAgent: 'Your agent', prepared: 'prepared', forYou: 'for you.',
    quote: 'Quote', validUntil: 'Valid until', badge: 'INSURANCE QUOTE',
    confirmAccept: 'Confirm: choose this plan? Your agent will be notified.',
    confirmDecline: 'Decline this quote? Your agent will be notified.',
    acceptFail: 'Could not accept: ', declineFail: 'Could not decline: ',
    chose: 'You chose this plan.',
    choseBody: 'Check your email — we just sent you a quick secure form to complete your enrollment.',
    notified: 'has been notified.', declined: 'You declined this quote.',
    selectedTag: '✓ SELECTED', perMonth: '/month', effective: 'Effective:',
    selected: '✓ Selected', saving: 'Saving…', alreadyChosen: 'Already chosen', choose: 'Choose this plan',
    sending: 'Sending…', decline: 'None of these — decline quote',
    attached: 'Attached document', openAttached: 'Open attached document', open: 'OPEN',
    notes: 'Notes from your agent', questions: 'Questions? Reply to the email or contact',
    yourAgentLower: 'your agent', openLink: 'Open ↗',
  },
  es: {
    loading: 'Cargando tu cotización…', notFound: 'Cotización no encontrada',
    notFoundBody: 'Este enlace puede ser inválido o haber vencido. Comunícate con tu agente.',
    hi: 'Hola', yourAgent: 'Tu agente', prepared: 'preparó', forYou: 'para ti.',
    quote: 'Cotización', validUntil: 'Válida hasta el', badge: 'COTIZACIÓN DE SEGURO',
    confirmAccept: '¿Confirmas que escoges este plan? Le avisaremos a tu agente.',
    confirmDecline: '¿Rechazar esta cotización? Le avisaremos a tu agente.',
    acceptFail: 'No se pudo aceptar: ', declineFail: 'No se pudo rechazar: ',
    chose: 'Escogiste este plan.',
    choseBody: 'Revisa tu correo: te acabamos de enviar un formulario seguro y corto para completar tu inscripción.',
    notified: 'ya fue notificado.', declined: 'Rechazaste esta cotización.',
    selectedTag: '✓ ESCOGIDO', perMonth: '/mes', effective: 'Vigente desde:',
    selected: '✓ Escogido', saving: 'Guardando…', alreadyChosen: 'Ya escogiste', choose: 'Escoger este plan',
    sending: 'Enviando…', decline: 'Ninguno me sirve — rechazar cotización',
    attached: 'Documento adjunto', openAttached: 'Abrir documento adjunto', open: 'ABRIR',
    notes: 'Notas de tu agente', questions: '¿Preguntas? Responde al correo o escribe a',
    yourAgentLower: 'tu agente', openLink: 'Abrir ↗',
  },
};

function fmtDate(d) {
  if (!d) return '';
  try {
    const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export function QuoteLandingPage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [accepting, setAccepting] = useState(null); // itemId being accepted
  const [accepted, setAccepted] = useState(null); // itemId that was accepted
  const [declined, setDeclined] = useState(false); // client declined the whole quote
  const [declining, setDeclining] = useState(false);
  // Antes de cargar no sabemos el idioma de la ficha: el del navegador.
  const navEs = typeof navigator !== 'undefined' && /^es/i.test(navigator.language || '');
  const es = data ? data.lang === 'es' : navEs;
  const t = TEXTOS[es ? 'es' : 'en'];

  useEffect(() => {
    setLoading(true);
    viewQuotePublic(token)
      .then((d) => {
        setData(d);
        if (d.acceptedItemId) setAccepted(d.acceptedItemId);
        if (d.status === 'DECLINED') setDeclined(true);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async (itemId) => {
    if (!confirm(t.confirmAccept)) return;
    setAccepting(itemId);
    try {
      await acceptQuoteOption(token, itemId);
      setAccepted(itemId);
    } catch (e) {
      toast.error(t.acceptFail + e.message);
    } finally {
      setAccepting(null);
    }
  };

  const handleDecline = async () => {
    if (!confirm(t.confirmDecline)) return;
    setDeclining(true);
    try {
      await declineQuote(token);
      setDeclined(true);
    } catch (e) {
      toast.error(t.declineFail + e.message);
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return <CenteredMessage>{t.loading}</CenteredMessage>;
  }
  if (err || !data) {
    return (
      <CenteredMessage>
        <h2 style={{ margin: '0 0 .5rem', color: '#ff3d6b' }}>{t.notFound}</h2>
        <p>{t.notFoundBody}</p>
      </CenteredMessage>
    );
  }

  const isClosed = data.status === 'CONVERTED' || data.status === 'EXPIRED' || data.status === 'DECLINED';

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg,#0a1628 0%,#0d1e36 100%)', color: '#e6f0fa', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {data.agencyLogoUrl && (
            <div style={{ marginBottom: 14 }}>
              <img src={data.agencyLogoUrl} alt={data.agency?.name || ''} style={{ maxHeight: 60, maxWidth: 220, objectFit: 'contain', background: '#fff', borderRadius: 12, padding: '8px 14px' }} />
            </div>
          )}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '.4rem 1rem', borderRadius: 999, background: 'rgba(0,212,255,.1)', color: '#00d4ff', fontSize: 12, fontWeight: 700, letterSpacing: '.5px' }}>
            <Shield size={14} /> {data.agency?.name || t.badge}
          </div>
          <h1 style={{ fontSize: '2rem', margin: '1rem 0 .3rem' }}>{t.hi} {data.recipientName},</h1>
          <p style={{ color: '#7aa3c0', margin: 0 }}>
            {data.agent?.name || t.yourAgent} {t.prepared} <strong style={{ color: '#e6f0fa' }}>{data.title}</strong> {t.forYou}
          </p>
          <p style={{ color: '#45688a', fontSize: 13, marginTop: 4 }}>
            {t.quote} {data.number} {data.validUntil && <>· {t.validUntil} {fmtDate(data.validUntil)}</>}
          </p>
        </div>

        {/* Household snapshot — the data (ZIP / income / member ages) these
            prices were computed from, so the client sees the basis. */}
        {data.householdInfo && (() => {
          const h = data.householdInfo;
          const members = Array.isArray(h.members) ? h.members : [];
          const covered = members.filter((m) => m?.wantsCoverage !== false);
          const ages = covered.map((m) => (m?.age != null ? m.age : '?')).join(', ');
          const rows = [
            h.zipcode ? ['ZIP', `${h.zipcode}${h.county ? ` (${h.county}${h.state ? `, ${h.state}` : ''})` : ''}`] : null,
            h.income != null ? ['Ingreso anual / Annual income', `$${Number(h.income).toLocaleString('en-US')}`] : null,
            members.length ? ['Personas en el hogar / Household size', String(members.length)] : null,
            covered.length ? ['Aseguran / Seeking coverage', `${covered.length}${ages ? ` — ${covered.length === 1 ? 'edad/age' : 'edades/ages'} ${ages}` : ''}`] : null,
            h.year ? ['Año del plan / Plan year', String(h.year)] : null,
          ].filter(Boolean);
          if (!rows.length) return null;
          return (
            <div style={{ background: 'rgba(0,212,255,.06)', border: '1px solid rgba(0,212,255,.35)', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#00d4ff', marginBottom: 8, letterSpacing: '.4px' }}>
                👥 DATOS DE ESTA COTIZACIÓN · QUOTE DATA
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '.4rem 1.2rem', fontSize: 13.5 }}>
                {rows.map(([label, value]) => (
                  <div key={label}>
                    <span style={{ color: '#7aa3c0' }}>{label}: </span>
                    <strong style={{ color: '#e6f0fa' }}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {accepted && (
          <div style={{ background: 'rgba(0,255,136,.1)', border: '1px solid #00ff88', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem', textAlign: 'center' }}>
            <CheckCircle size={20} style={{ color: '#00ff88', verticalAlign: -4, marginRight: 6 }} />
            <strong style={{ color: '#00ff88' }}>{t.chose}</strong> {t.choseBody} {data.agent?.name || t.yourAgent} {t.notified}
          </div>
        )}

        {declined && !accepted && (
          <div style={{ background: 'rgba(255,61,107,.08)', border: '1px solid #ff3d6b', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem', textAlign: 'center' }}>
            <strong style={{ color: '#ff3d6b' }}>{t.declined}</strong> {data.agent?.name || t.yourAgent} {t.notified}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(280px,1fr))`, gap: '1rem', marginBottom: '2rem' }}>
          {data.items.map((it) => {
            const isAccepted = it.id === accepted;
            const disabled = !!accepted || isClosed || declined;
            return (
              <div
                key={it.id}
                style={{
                  background: isAccepted ? 'rgba(0,255,136,.08)' : 'rgba(255,255,255,.04)',
                  border: `2px solid ${isAccepted ? '#00ff88' : 'rgba(122,163,192,.2)'}`,
                  borderRadius: 14,
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '.5rem',
                  position: 'relative',
                }}
              >
                {isAccepted && (
                  <div style={{ position: 'absolute', top: 12, right: 12, background: '#00ff88', color: '#0a1628', padding: '.2rem .6rem', borderRadius: 999, fontSize: 11, fontWeight: 800 }}>
                    {t.selectedTag}
                  </div>
                )}
                {it.carrier && (
                  <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px' }}>{it.carrier}</div>
                )}
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{it.description}</h3>
                {it.planType && <div style={{ fontSize: 12, color: '#45688a' }}>{it.planTypeLabel || String(it.planType).replace(/_/g, ' ')}</div>}
                <div style={{ margin: '.4rem 0' }}>
                  <span style={{ fontSize: '2.4rem', fontWeight: 800, color: '#00d4ff' }}>{fmtMoney(it.unitPrice)}</span>
                  <span style={{ color: '#7aa3c0', fontSize: 13 }}>{t.perMonth}</span>
                </div>
                {it.effectiveDate && (
                  <div style={{ fontSize: 12, color: '#7aa3c0' }}>
                    <Calendar size={11} style={{ verticalAlign: -1 }} /> {t.effective} {fmtDate(it.effectiveDate)}
                  </div>
                )}
                {it.coverageDetails && publicCoverageFieldsFor(it.planType, it.coverageDetails).some((f) => it.coverageDetails[f.key]) && (
                  <div style={{ borderTop: '1px solid rgba(122,163,192,.2)', paddingTop: '.6rem', marginTop: '.4rem', fontSize: 13 }}>
                    {publicCoverageFieldsFor(it.planType, it.coverageDetails).map((f) => (
                      it.coverageDetails[f.key]
                        ? <CovRow key={f.key} label={coverageLabel(f.key, es)} value={it.coverageDetails[f.key]} abrir={t.openLink} />
                        : null
                    ))}
                  </div>
                )}
                <DetalleDeCobertura it={it} es={es} />
                <button
                  onClick={() => handleAccept(it.id)}
                  disabled={disabled || accepting === it.id}
                  style={{
                    marginTop: 'auto',
                    padding: '.7rem 1rem',
                    borderRadius: 10,
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: disabled ? 'default' : 'pointer',
                    background: isAccepted ? '#00ff88' : (disabled ? 'rgba(122,163,192,.15)' : '#00d4ff'),
                    color: isAccepted || !disabled ? '#0a1628' : '#7aa3c0',
                    opacity: accepting === it.id ? 0.6 : 1,
                  }}
                >
                  {isAccepted ? t.selected : accepting === it.id ? t.saving : disabled ? t.alreadyChosen : t.choose}
                </button>
              </div>
            );
          })}
        </div>

        {!accepted && !declined && !isClosed && (
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <button
              onClick={handleDecline}
              disabled={declining}
              style={{ background: 'transparent', border: '1px solid rgba(255,61,107,.5)', color: '#ff7da0', padding: '.6rem 1.2rem', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: declining ? 'default' : 'pointer', opacity: declining ? 0.6 : 1 }}
            >
              {declining ? t.sending : t.decline}
            </button>
          </div>
        )}

        {/* Los documentos que adjunto el agente. Antes solo cabia uno; ahora
            vienen en lista, y una cotizacion anterior a ese cambio llega con
            su unico archivo en attachmentUrl — de ahi el respaldo de abajo,
            para que un enlace ya enviado a un cliente siga abriendo su PDF. */}
        {(Array.isArray(data.attachments) && data.attachments.length
          ? data.attachments
          : (data.attachmentUrl ? [{ url: data.attachmentUrl, name: data.attachmentName }] : [])
        ).filter((a) => a.url).map((a, i) => (
          <a
            key={a.url || i}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '.7rem',
              background: 'rgba(0,212,255,.08)',
              border: '1px solid #00d4ff',
              borderRadius: 12,
              padding: '1rem',
              marginBottom: '.6rem',
              textDecoration: 'none',
              color: '#e6f0fa',
            }}
          >
            <FileDown size={22} style={{ color: '#00d4ff', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px' }}>{t.attached}</div>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name || t.openAttached}</div>
            </div>
            <span style={{ fontSize: 12, color: '#00d4ff', fontWeight: 700 }}>{t.open} &rarr;</span>
          </a>
        ))}

        {data.notes && (
          <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,163,192,.2)', borderRadius: 12, padding: '1rem', marginBottom: '2rem' }}>
            <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '.4rem' }}>{t.notes}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{data.notes}</div>
          </div>
        )}

        <div style={{ textAlign: 'center', color: '#7aa3c0', fontSize: 13, marginTop: '3rem' }}>
          <Mail size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
          {t.questions}{' '}
          {data.agent?.email ? <a href={`mailto:${data.agent.email}`} style={{ color: '#00d4ff' }}>{data.agent.email}</a> : t.yourAgentLower}.
          <div style={{ marginTop: '1rem', fontSize: 11, color: '#45688a' }}>
            Powered by Deskte · Insurance Agent CRM
          </div>
        </div>
      </div>
    </div>
  );
}

/** Cuánto paga el plan y lo que NO cubre (lo pone el cierre de la asesoría). */
function DetalleDeCobertura({ it, es }) {
  const { filas, noCubre, lePaga, folletos } = leerDetalleDeCotizacion(it.coverageDetails);
  const conEnlace = folletos.filter((f) => f.url);
  if (!filas.length && !noCubre.length && !conEnlace.length) return null;
  return (
    <div style={{ borderTop: '1px solid rgba(122,163,192,.2)', paddingTop: '.6rem', marginTop: '.4rem', fontSize: 13 }}>
      {filas.length > 0 && <div style={{ fontWeight: 700, color: '#e6f0fa', marginBottom: '.2rem' }}>{tituloDelDetalle(lePaga, es)}</div>}
      {filas.map((f, i) => <CovRow key={i} label={f.concepto} value={f.valor} abrir="" />)}
      {noCubre.length > 0 && (
        <div style={{ marginTop: '.5rem' }}>
          <div style={{ fontWeight: 700, color: '#ff8a8a', marginBottom: '.15rem' }}>{es ? 'No cubre' : 'Not covered'}</div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem', color: '#7aa3c0' }}>{noCubre.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </div>
      )}
      {conEnlace.length > 0 && (
        <div style={{ marginTop: '.5rem' }}>
          <div style={{ fontWeight: 700, color: '#e6f0fa', marginBottom: '.15rem' }}>{es ? 'Folletos' : 'Brochures'}</div>
          {conEnlace.map((f, i) => (
            <div key={i}><a href={f.url} target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff', fontWeight: 600 }}>{f.nombre} ↗</a></div>
          ))}
        </div>
      )}
    </div>
  );
}

function CovRow({ label, value, abrir }) {
  // A URL value (e.g. the official brochure) is shown as a tidy "Open" link
  // instead of dumping the raw, overflowing URL into the row.
  const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value.trim());
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.15rem 0', color: '#7aa3c0', gap: 8 }}>
      <span>{label}</span>
      {isUrl
        ? <a href={value.trim()} target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff', fontWeight: 600, whiteSpace: 'nowrap' }}>{abrir}</a>
        : <strong style={{ color: '#e6f0fa', textAlign: 'right' }}>{value}</strong>}
    </div>
  );
}

function CenteredMessage({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#e6f0fa', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>{children}</div>
    </div>
  );
}
